import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export interface User {
  userId: string;
  username: string;
  email?: string;
  [key: string]: any;
}

interface AuthResult {
  success: boolean;
  error?: string;
  user?: User;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<AuthResult>;
  register: (userData: {
    username: string;
    email?: string;
    password: string;
    [key: string]: any;
  }) => Promise<AuthResult>;
  logout: () => void;
  isAuthenticated: () => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth manager (lazy loaded to ensure entity service is ready)
  const getAuthManager = () => {
    // Import dynamically to avoid issues if database isn't enabled
    try {
      const { episoloCreateAuthManager } = require("@/lib/episolo-db");
      return episoloCreateAuthManager();
    } catch (error) {
      console.warn("Auth manager not available - database may not be enabled");
      return null;
    }
  };

  // Check for existing user on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const authManager = getAuthManager();
        if (authManager) {
          const currentUser = authManager.getCurrentUser();
          setUser(currentUser);
        }
      } catch (error) {
        console.warn("Could not check authentication status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Small delay to ensure entity service is initialized
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, []);

  const login = async (
    username: string,
    password: string,
  ): Promise<AuthResult> => {
    try {
      const authManager = getAuthManager();
      if (!authManager) {
        return { success: false, error: "Authentication not available" };
      }

      const result = await authManager.login(username, password);
      if (result.success) {
        setUser(result.user);
      }
      return result;
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Login failed" };
    }
  };

  const register = async (userData: {
    username: string;
    email?: string;
    password: string;
    [key: string]: any;
  }): Promise<AuthResult> => {
    try {
      const authManager = getAuthManager();
      if (!authManager) {
        return { success: false, error: "Authentication not available" };
      }

      const result = await authManager.register(userData);
      if (result.success) {
        setUser(result.user);
      }
      return result;
    } catch (error) {
      console.error("Registration error:", error);
      return { success: false, error: "Registration failed" };
    }
  };

  const logout = () => {
    try {
      const authManager = getAuthManager();
      if (authManager) {
        authManager.logout();
      }
      setUser(null);
    } catch (error) {
      console.warn("Logout error:", error);
      setUser(null); // Force logout even if there's an error
    }
  };

  const isAuthenticated = (): boolean => {
    return user !== null;
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
